import { parseFieldDefinitionIdFromWorkflowToken } from "../data-ingestion/ingestion.hydrate.util";
import {
    INGESTION_POINT_TYPE_ID,
    REQUIRES_EXPLICIT_ACCEPTED_FIELD_TYPES,
} from "../data-ingestion/data.ingestion.const";
import {
    AnswerType,
    DataIngestionService,
    FieldType,
    IngestionFieldDefinition,
} from "../data-ingestion/data.ingestion.service";
import { DataAccess } from "../../dataaccess/data.access";
import { PreferenceService } from "../../services/preference.service";
import { S25Util } from "../../util/s25-util";
import { Workflow } from "./service/Workflow";
import { WorkflowService } from "./service/workflow.service";
import { WorkflowUtil } from "./workflow.util";
import { AiTelemetry } from "../../services/ai.telemetry";

const WORKFLOW_FIELD_REF_REGEX = /\$CNET-(-?\d+)-\w+/g;

/** Action inputs that must be JSON documents — never bare $CNET field references (mirrors 25saas WorkflowActionInputMappingRules). */
const STRUCTURED_JSON_INPUT_CODES = new Set([
    "contactRoleFieldRefs",
    "actionConfig",
    "orgCategoryIds",
    "categoryIds",
    "requirementIds",
    "additionalOrgIds",
    "orgCustomAttributes",
    "customAttributes",
    "registrants",
]);

export const ACTION_INPUT_MAP_CONFIDENCE_THRESHOLD = 0.85;

export type AutoMapMode = "automatic" | "explicit";

export type ActionInputMapRequest = {
    actionCode: string;
    actionDisplayName?: string;
    ingestionPointId?: number;
    inputs: {
        inputCode: string;
        label: string;
        help?: string;
        required?: boolean;
        acceptedAnswerTypes?: string[];
        acceptedFieldTypes?: string[];
    }[];
    targetFormFields: {
        fieldDefinitionId: number;
        fieldLabel: string;
        fieldCode?: string;
        reportingFieldCode?: string;
        answerType?: string;
        fieldType?: string;
    }[];
};

export type ActionInputMapResponse = {
    mappings: { inputCode: string; fieldDefinitionId: number; confidence: number }[];
    error?: string;
};

type ActionInputMapResponseRoot = {
    ActionInputMapResponse: ActionInputMapResponse;
};

function normalizeKey(value: string | undefined): string {
    return (value ?? "").trim().toLowerCase();
}

function buildTargetFieldsByReportingCode(targetFields: IngestionFieldDefinition[]): {
    byReportingCode: Map<string, IngestionFieldDefinition>;
    byId: Map<number, IngestionFieldDefinition>;
} {
    const byReportingCode = new Map<string, IngestionFieldDefinition>();
    const byId = new Map<number, IngestionFieldDefinition>();

    for (const field of targetFields) {
        if (field.fieldDefinitionId == null) continue;
        byId.set(field.fieldDefinitionId, field);
        const reporting = normalizeKey(field.reportingFieldCode);
        if (reporting && !byReportingCode.has(reporting)) {
            byReportingCode.set(reporting, field);
        }
    }

    return { byReportingCode, byId };
}

/** Cross-form donor remap: match target field by reportingFieldCode only (not label or generated field code). */
function resolveTargetFieldByReportingCode(
    donorField: IngestionFieldDefinition,
    byReportingCode: Map<string, IngestionFieldDefinition>,
): IngestionFieldDefinition | undefined {
    const reporting = normalizeKey(donorField.reportingFieldCode);
    if (!reporting) {
        return undefined;
    }
    return byReportingCode.get(reporting);
}

function getInputSpecForCode(actionCode: string, inputCode: string): Workflow.SystemActionInput | undefined {
    const meta = WorkflowService.SYSTEM_ACTIONS[actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
    if (!meta || !("inputs" in meta) || !meta.inputs) return undefined;
    return meta.inputs.find((row) => row.inputCode === inputCode);
}

function getMappableInputSpecs(actionCode: string): Workflow.SystemActionInput[] {
    const meta = WorkflowService.SYSTEM_ACTIONS[actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
    if (!meta || !("inputs" in meta) || !meta.inputs) return [];
    return (meta.inputs as Workflow.SystemActionInput[]).filter(
        (spec) => !STRUCTURED_JSON_INPUT_CODES.has(spec.inputCode),
    );
}

function isPersistedFieldDefinition(field: IngestionFieldDefinition): boolean {
    return field.fieldDefinitionId != null && field.fieldDefinitionId > 0;
}

function effectiveAcceptedFieldTypes(inputSpec: Workflow.SystemActionInput | undefined): FieldType[] | undefined {
    if (inputSpec?.acceptedFieldTypes?.length) {
        return inputSpec.acceptedFieldTypes;
    }
    return undefined;
}

function passesTypeConstraints(
    targetField: IngestionFieldDefinition,
    inputSpec: Workflow.SystemActionInput | undefined,
): boolean {
    if (!isPersistedFieldDefinition(targetField)) {
        return false;
    }
    if (!inputSpec) return true;

    const acceptedFieldTypes = effectiveAcceptedFieldTypes(inputSpec);
    if (acceptedFieldTypes?.length) {
        const token = WorkflowUtil.getFieldReference(targetField);
        const errors = WorkflowUtil.validateFieldReferences(
            token,
            [targetField],
            inputSpec.acceptedAnswerTypes,
            acceptedFieldTypes,
        );
        return errors.length === 0;
    }

    if (targetField.fieldType && REQUIRES_EXPLICIT_ACCEPTED_FIELD_TYPES.has(targetField.fieldType)) {
        return false;
    }

    const token = WorkflowUtil.getFieldReference(targetField);
    const errors = WorkflowUtil.validateFieldReferences(token, [targetField], inputSpec.acceptedAnswerTypes, undefined);
    return errors.length === 0;
}

function isInputValueEmpty(value: string | undefined): boolean {
    return value == null || String(value).trim() === "";
}

function buildTargetFieldIdSet(targetFields: IngestionFieldDefinition[]): Set<number> {
    const targetFieldIds = new Set<number>();
    for (const field of targetFields) {
        if (isPersistedFieldDefinition(field)) {
            targetFieldIds.add(field.fieldDefinitionId!);
        }
    }
    return targetFieldIds;
}

/** True when empty or when a $CNET-n- token points at a field id not on the target form (e.g. cloned from another form). */
function inputNeedsRemappingForTarget(value: string | undefined, targetFieldIds: Set<number>): boolean {
    if (isInputValueEmpty(value)) {
        return true;
    }
    if (!value?.includes("$CNET-")) {
        return false;
    }
    const tokens = [...value.matchAll(WORKFLOW_FIELD_REF_REGEX)];
    if (!tokens.length) {
        return false;
    }
    return tokens.some((match) => {
        const fieldDefinitionId = parseInt(match[1], 10);
        return fieldDefinitionId > 0 && !targetFieldIds.has(fieldDefinitionId);
    });
}

function hasValidFieldReferenceMapping(value: string | undefined, targetFieldIds: Set<number>): boolean {
    if (isInputValueEmpty(value) || !value?.includes("$CNET-")) {
        return false;
    }
    return !inputNeedsRemappingForTarget(value, targetFieldIds);
}

/** Clear $CNET references to field ids that are not on the target form. Returns true if any input was cleared. */
export function clearStaleFieldReferences(
    action: Workflow.WorkflowAction,
    targetFields: IngestionFieldDefinition[],
): boolean {
    const targetFieldIds = buildTargetFieldIdSet(targetFields);
    let cleared = false;
    for (const input of action.inputs ?? []) {
        if (!input.inputValue?.includes("$CNET-")) {
            continue;
        }
        if (inputNeedsRemappingForTarget(input.inputValue, targetFieldIds)) {
            input.inputValue = "";
            cleared = true;
        }
    }
    return cleared;
}

export function collectMappedFieldDefinitionIds(
    action: Workflow.WorkflowAction,
    targetFieldIds: Set<number>,
): Set<number> {
    const mapped = new Set<number>();
    for (const input of action.inputs ?? []) {
        if (!input.inputValue?.includes("$CNET-")) {
            continue;
        }
        for (const match of input.inputValue.matchAll(WORKFLOW_FIELD_REF_REGEX)) {
            const fieldDefinitionId = parseInt(match[1], 10);
            if (fieldDefinitionId > 0 && targetFieldIds.has(fieldDefinitionId)) {
                mapped.add(fieldDefinitionId);
            }
        }
    }
    return mapped;
}

export function hasAnyValidFieldReferenceMapping(
    action: Workflow.WorkflowAction,
    targetFields: IngestionFieldDefinition[],
): boolean {
    const actionCode = action.actionCode;
    if (!actionCode) {
        return false;
    }
    const targetFieldIds = buildTargetFieldIdSet(targetFields);
    for (const spec of getMappableInputSpecs(actionCode)) {
        const value = getInputValue(action, spec.inputCode);
        if (hasValidFieldReferenceMapping(value, targetFieldIds)) {
            return true;
        }
    }
    return false;
}

export function actionInputsNeedAutoMapping(
    action: Workflow.WorkflowAction,
    targetFields: IngestionFieldDefinition[],
): boolean {
    const actionCode = action.actionCode;
    if (!actionCode) {
        return false;
    }
    const targetFieldIds = buildTargetFieldIdSet(targetFields);
    for (const spec of getMappableInputSpecs(actionCode)) {
        if (inputNeedsRemappingForTarget(getInputValue(action, spec.inputCode), targetFieldIds)) {
            return true;
        }
    }
    return false;
}

type ScoredMappingCandidate = {
    inputCode: string;
    fieldDefinitionId: number;
    confidence: number;
};

/** Winner-takes-all by confidence; each field and input used at most once. Seeds from reservedFieldIds. */
function selectWinnerTakesAll(
    candidates: ScoredMappingCandidate[],
    reservedFieldIds: Set<number> = new Set(),
): ScoredMappingCandidate[] {
    const sorted = [...candidates].sort((left, right) => right.confidence - left.confidence);

    const usedInputCodes = new Set<string>();
    const usedFieldIds = new Set<number>(reservedFieldIds);
    const winners: ScoredMappingCandidate[] = [];

    for (const candidate of sorted) {
        if (usedInputCodes.has(candidate.inputCode) || usedFieldIds.has(candidate.fieldDefinitionId)) {
            continue;
        }
        usedInputCodes.add(candidate.inputCode);
        usedFieldIds.add(candidate.fieldDefinitionId);
        winners.push(candidate);
    }

    return winners;
}

function getInputValue(action: Workflow.WorkflowAction, inputCode: string): string | undefined {
    return action.inputs?.find((row) => row.inputCode === inputCode)?.inputValue;
}

function setInputValue(action: Workflow.WorkflowAction, inputCode: string, value: string): void {
    if (!action.inputs) {
        action.inputs = [];
    }
    let row = action.inputs.find((input) => input.inputCode === inputCode);
    if (!row) {
        row = { id: -Date.now(), inputCode, inputValue: value };
        action.inputs.push(row);
    } else {
        row.inputValue = value;
    }
}

function collectDonorActions(
    workflowProcess: Workflow.WorkflowProcess,
    targetAction: Workflow.WorkflowAction,
    targetFormId: number | undefined,
): Workflow.WorkflowAction[] {
    const actionCode = targetAction.actionCode;
    if (!actionCode) return [];

    const donors: Workflow.WorkflowAction[] = [];
    for (const stage of workflowProcess.stages ?? []) {
        for (const action of stage.actions ?? []) {
            if (action === targetAction) continue;
            if (action.actionCode !== actionCode) continue;
            if (Number(action.actionItemTypeId) !== INGESTION_POINT_TYPE_ID) continue;
            if (targetFormId != null && Number(action.actionItemId) === targetFormId) continue;
            donors.push(action);
        }
    }
    return donors;
}

async function loadFieldDefinitionsByIngestionPoint(
    ingestionPointIds: Iterable<number>,
    cache: Map<number, IngestionFieldDefinition[]>,
): Promise<void> {
    const missing: number[] = [];
    for (const id of ingestionPointIds) {
        if (!cache.has(id)) {
            missing.push(id);
        }
    }
    await Promise.all(
        missing.map(async (ingestionPointId) => {
            const [fields, error] = await S25Util.Maybe(DataIngestionService.getFieldDefinitions(ingestionPointId));
            cache.set(ingestionPointId, error ? [] : (fields ?? []));
        }),
    );
}

function findDonorField(
    fieldDefinitionId: number,
    donorFields: IngestionFieldDefinition[],
): IngestionFieldDefinition | undefined {
    return donorFields.find((field) => field.fieldDefinitionId === fieldDefinitionId);
}

async function applyReportingCodePass(
    targetAction: Workflow.WorkflowAction,
    targetFields: IngestionFieldDefinition[],
    workflowProcess: Workflow.WorkflowProcess,
    fieldCache: Map<number, IngestionFieldDefinition[]>,
    reservedFieldIds: Set<number>,
): Promise<void> {
    const actionCode = targetAction.actionCode;
    if (!actionCode) return;

    const targetMaps = buildTargetFieldsByReportingCode(targetFields);
    const targetFieldIds = buildTargetFieldIdSet(targetFields);
    const donors = collectDonorActions(workflowProcess, targetAction, targetAction.actionItemId);

    const donorIngestionPointIds = new Set<number>();
    for (const donor of donors) {
        if (donor.actionItemId) {
            donorIngestionPointIds.add(Number(donor.actionItemId));
        }
    }

    const meta = WorkflowService.SYSTEM_ACTIONS[actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
    const inputCodes = new Set<string>();
    for (const donor of donors) {
        for (const input of donor.inputs ?? []) {
            if (input.inputCode) {
                inputCodes.add(input.inputCode);
            }
        }
    }
    if (meta && "inputs" in meta && meta.inputs) {
        for (const inputDef of meta.inputs) {
            inputCodes.add(inputDef.inputCode);
        }
    }

    const donorByInputCode = new Map<string, Workflow.WorkflowAction[]>();
    for (const donor of donors) {
        for (const input of donor.inputs ?? []) {
            if (!input.inputCode || !input.inputValue?.includes("$CNET-")) continue;
            const list = donorByInputCode.get(input.inputCode) ?? [];
            list.push(donor);
            donorByInputCode.set(input.inputCode, list);
        }
    }

    await loadFieldDefinitionsByIngestionPoint(donorIngestionPointIds, fieldCache);

    const reportingCandidates: ScoredMappingCandidate[] = [];

    for (const inputCode of inputCodes) {
        if (STRUCTURED_JSON_INPUT_CODES.has(inputCode)) {
            continue;
        }
        if (!inputNeedsRemappingForTarget(getInputValue(targetAction, inputCode), targetFieldIds)) {
            continue;
        }

        const donorList = donorByInputCode.get(inputCode);
        if (!donorList?.length) continue;

        const inputSpec = getInputSpecForCode(actionCode, inputCode);

        for (const donor of donorList) {
            const donorInput = donor.inputs?.find((row) => row.inputCode === inputCode);
            const token = donorInput?.inputValue?.match(WORKFLOW_FIELD_REF_REGEX)?.[0];
            if (!token) continue;

            const donorFieldId = parseFieldDefinitionIdFromWorkflowToken(token);
            if (donorFieldId == null || !donor.actionItemId) continue;

            const donorFields = fieldCache.get(Number(donor.actionItemId)) ?? [];
            const donorField = findDonorField(donorFieldId, donorFields);
            if (!donorField) continue;

            const targetField = resolveTargetFieldByReportingCode(donorField, targetMaps.byReportingCode);
            if (!targetField?.fieldDefinitionId) continue;

            if (reservedFieldIds.has(targetField.fieldDefinitionId)) continue;

            if (!passesTypeConstraints(targetField, inputSpec)) continue;

            reportingCandidates.push({
                inputCode,
                fieldDefinitionId: targetField.fieldDefinitionId,
                confidence: 1,
            });
            break;
        }
    }

    for (const winner of selectWinnerTakesAll(reportingCandidates, reservedFieldIds)) {
        const targetField = targetMaps.byId.get(winner.fieldDefinitionId);
        if (!targetField) continue;
        setInputValue(targetAction, winner.inputCode, WorkflowUtil.getFieldReference(targetField));
        reservedFieldIds.add(winner.fieldDefinitionId);
    }
}

function answerTypeLabel(type: AnswerType | undefined): string | undefined {
    if (type == null) return undefined;
    return WorkflowUtil.answerTypeLabel(type);
}

function buildAiRequest(
    targetAction: Workflow.WorkflowAction,
    targetFields: IngestionFieldDefinition[],
    reservedFieldIds: Set<number>,
): ActionInputMapRequest | null {
    const actionCode = targetAction.actionCode;
    if (!actionCode) return null;

    const meta = WorkflowService.SYSTEM_ACTIONS[actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
    const displayName = "displayName" in meta ? meta.displayName : actionCode;

    const targetFieldIds = buildTargetFieldIdSet(targetFields);
    const unmappedInputs: ActionInputMapRequest["inputs"] = [];
    const specInputs: Workflow.SystemActionInput[] =
        "inputs" in meta && meta.inputs ? (meta.inputs as Workflow.SystemActionInput[]) : [];

    for (const spec of specInputs) {
        if (STRUCTURED_JSON_INPUT_CODES.has(spec.inputCode)) {
            continue;
        }
        if (!inputNeedsRemappingForTarget(getInputValue(targetAction, spec.inputCode), targetFieldIds)) {
            continue;
        }
        unmappedInputs.push({
            inputCode: spec.inputCode,
            label: spec.label,
            help: spec.help,
            required: spec.required,
            acceptedAnswerTypes: spec.acceptedAnswerTypes?.map((type) => answerTypeLabel(type) ?? String(type)),
            acceptedFieldTypes: spec.acceptedFieldTypes,
        });
    }

    if (!unmappedInputs.length) {
        return null;
    }

    const targetFormFields = targetFields
        .filter(
            (field) =>
                isPersistedFieldDefinition(field) &&
                !field.isDeleted &&
                !reservedFieldIds.has(field.fieldDefinitionId!),
        )
        .map((field) => ({
            fieldDefinitionId: field.fieldDefinitionId!,
            fieldLabel: field.fieldLabel ?? "",
            fieldCode: field.fieldCode ?? WorkflowUtil.getFieldCode(field),
            reportingFieldCode: field.reportingFieldCode,
            answerType: field.answerType != null ? answerTypeLabel(field.answerType) : undefined,
            fieldType: field.fieldType,
        }));

    if (!targetFormFields.length) {
        return null;
    }

    return {
        actionCode,
        actionDisplayName: displayName,
        ingestionPointId: targetAction.actionItemId != null ? Number(targetAction.actionItemId) : undefined,
        inputs: unmappedInputs,
        targetFormFields,
    };
}

function unwrapActionInputMapResponse(
    raw: ActionInputMapResponse | ActionInputMapResponseRoot | undefined,
): ActionInputMapResponse | undefined {
    if (!raw) return undefined;
    if ("ActionInputMapResponse" in raw) return raw.ActionInputMapResponse;
    return raw;
}

async function isWorkflowFormAssistanceEnabled(): Promise<boolean> {
    try {
        const prefs = await PreferenceService.getPreferences(["config_ai_form_workflow_builder"], "S");
        return prefs?.config_ai_form_workflow_builder?.value === "T";
    } catch {
        return false;
    }
}

async function applyAiPass(
    targetAction: Workflow.WorkflowAction,
    targetFields: IngestionFieldDefinition[],
    reservedFieldIds: Set<number>,
): Promise<void> {
    if (!(await isWorkflowFormAssistanceEnabled())) {
        return;
    }

    const request = buildAiRequest(targetAction, targetFields, reservedFieldIds);
    if (!request) return;

    AiTelemetry.track("WorkflowActionMap", "Engage");

    try {
        const response = await DataAccess.post<ActionInputMapResponse | ActionInputMapResponseRoot>(
            "/ai/workflow/map-action-inputs.json",
            { ActionInputMapRequest: request },
        );
        const mapResponse = unwrapActionInputMapResponse(response);
        if (mapResponse?.error) {
            console.warn("AI action input mapping skipped:", mapResponse.error);
            AiTelemetry.track("WorkflowActionMap", "Error");
            return;
        }
        const mappings = mapResponse?.mappings;
        if (!mappings?.length) {
            return;
        }

        const actionCode = targetAction.actionCode;
        if (!actionCode) return;

        const targetFieldIds = buildTargetFieldIdSet(targetFields);
        const fieldsById = new Map(
            targetFields
                .filter((field) => field.fieldDefinitionId != null)
                .map((field) => [field.fieldDefinitionId!, field]),
        );

        const aiCandidates: ScoredMappingCandidate[] = [];
        for (const row of mappings) {
            if (row.confidence < ACTION_INPUT_MAP_CONFIDENCE_THRESHOLD) continue;
            if (!inputNeedsRemappingForTarget(getInputValue(targetAction, row.inputCode), targetFieldIds)) {
                continue;
            }
            if (reservedFieldIds.has(row.fieldDefinitionId)) continue;

            const targetField = fieldsById.get(row.fieldDefinitionId);
            if (!targetField) continue;

            const inputSpec = getInputSpecForCode(actionCode, row.inputCode);
            if (!passesTypeConstraints(targetField, inputSpec)) continue;

            aiCandidates.push({
                inputCode: row.inputCode,
                fieldDefinitionId: row.fieldDefinitionId,
                confidence: row.confidence,
            });
        }

        let mappingsApplied = 0;
        for (const winner of selectWinnerTakesAll(aiCandidates, reservedFieldIds)) {
            const targetField = fieldsById.get(winner.fieldDefinitionId);
            if (!targetField) continue;
            setInputValue(targetAction, winner.inputCode, WorkflowUtil.getFieldReference(targetField));
            reservedFieldIds.add(winner.fieldDefinitionId);
            mappingsApplied++;
        }
        if (mappingsApplied > 0) {
            AiTelemetry.track("WorkflowActionMap", "Complete");
        }
    } catch (caughtError) {
        console.error("AI action input mapping failed:", caughtError);
        AiTelemetry.track("WorkflowActionMap", "Error");
    }
}

export class WorkflowActionAutoMapService {
    static async autoMapActionInputs(
        targetAction: Workflow.WorkflowAction,
        targetFields: IngestionFieldDefinition[],
        workflowProcess: Workflow.WorkflowProcess,
        fieldCache: Map<number, IngestionFieldDefinition[]> = new Map(),
        options?: { mode?: AutoMapMode },
    ): Promise<void> {
        const mode = options?.mode ?? "automatic";
        try {
            if (targetFields?.length) {
                fieldCache.set(Number(targetAction.actionItemId), targetFields);
            }

            clearStaleFieldReferences(targetAction, targetFields);

            if (mode === "automatic" && hasAnyValidFieldReferenceMapping(targetAction, targetFields)) {
                return;
            }
            if (mode === "explicit" && !actionInputsNeedAutoMapping(targetAction, targetFields)) {
                return;
            }

            const targetFieldIds = buildTargetFieldIdSet(targetFields);
            const reservedFieldIds = collectMappedFieldDefinitionIds(targetAction, targetFieldIds);

            await applyReportingCodePass(targetAction, targetFields, workflowProcess, fieldCache, reservedFieldIds);
            await applyAiPass(targetAction, targetFields, reservedFieldIds);

            clearStaleFieldReferences(targetAction, targetFields);
        } catch (caughtError) {
            console.error("Action input auto-mapping failed:", caughtError);
        }
    }
}
